import jsPDF from 'jspdf';
import {
  ConsolidatedReportDTO,
  SigmaResumenMesDTO,
  CvResumenMesDTO,
  GeneralInfoRowDTO,
  IndicatorAnalitoDTO,
  SigmaHistoryRowDTO,
  CvHistoryRowDTO,
  QcPlanningRowDTO,
} from '../../types/types.qualityControl';

// ─── Paleta de bandas Sigma ───
const COLOR = {
  WORLD: [76, 175, 80] as [number, number, number],
  EXCELLENT: [33, 150, 243] as [number, number, number],
  GOOD: [255, 193, 7] as [number, number, number],
  MIN_ACCEPT: [255, 152, 0] as [number, number, number],
  UNACCEPT: [244, 67, 54] as [number, number, number],
  CRITICAL: [183, 28, 28] as [number, number, number],
  GREY: [158, 158, 158] as [number, number, number],
  TEXT: [33, 33, 33] as [number, number, number],
  SOFT: [120, 120, 120] as [number, number, number],
  HEADER_BG: [3, 169, 244] as [number, number, number],
  ROW_ALT: [245, 247, 250] as [number, number, number],
  BORDER: [220, 224, 230] as [number, number, number],
};

const sigmaBandRGB = (sigma: number | null | undefined): [number, number, number] => {
  if (sigma == null || isNaN(sigma)) return COLOR.GREY;
  if (sigma >= 6) return COLOR.WORLD;
  if (sigma >= 5) return COLOR.EXCELLENT;
  if (sigma >= 4) return COLOR.GOOD;
  if (sigma >= 3) return COLOR.MIN_ACCEPT;
  if (sigma >= 2) return COLOR.UNACCEPT;
  return COLOR.CRITICAL;
};

const indexRGB = (val: number | null | undefined): [number, number, number] => {
  if (val == null || isNaN(val)) return COLOR.GREY;
  return val <= 1.0 ? COLOR.WORLD : COLOR.UNACCEPT;
};

// ─── Layout / paginación ───
const PAGE_W = 297; // landscape A4 en mm
const PAGE_H = 210;
const MARGIN_X = 10;
const MARGIN_TOP = 14;
const MARGIN_BOTTOM = 12;
const FOOTER_Y = PAGE_H - 6;

interface PdfContext {
  doc: jsPDF;
  cursorY: number;
  page: number;
  totalPages: number;
}

const setFill = (doc: jsPDF, rgb: [number, number, number]) => doc.setFillColor(rgb[0], rgb[1], rgb[2]);
const setText = (doc: jsPDF, rgb: [number, number, number]) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
const setDraw = (doc: jsPDF, rgb: [number, number, number]) => doc.setDrawColor(rgb[0], rgb[1], rgb[2]);

const formatDate = (iso: string): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  } catch {
    return iso;
  }
};

const formatDateTime = (iso: string): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return `${formatDate(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return iso;
  }
};

const num = (v: any, decimals = 2): string => {
  if (v == null || isNaN(Number(v))) return '—';
  return Number(v).toFixed(decimals);
};

// ─── Encabezado de página ───
const drawPageHeader = (ctx: PdfContext, mainTitle: string, subtitle: string, data: ConsolidatedReportDTO) => {
  const { doc } = ctx;
  setFill(doc, COLOR.HEADER_BG);
  doc.rect(0, 0, PAGE_W, 9, 'F');

  setText(doc, [255, 255, 255]);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(mainTitle, MARGIN_X, 6);

  setText(doc, [255, 255, 255]);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  const periodo = `${formatDate(data.FechaDesde)} — ${formatDate(data.FechaHasta)}`;
  doc.text(periodo, PAGE_W - MARGIN_X, 6, { align: 'right' });

  setText(doc, COLOR.TEXT);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(subtitle, MARGIN_X, MARGIN_TOP);

  ctx.cursorY = MARGIN_TOP + 4;
};

// ─── Pie de página ───
const drawPageFooter = (ctx: PdfContext) => {
  const { doc, page, totalPages } = ctx;
  setText(doc, COLOR.SOFT);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('SynerLab — Informes Six Sigma de Control de Calidad', MARGIN_X, FOOTER_Y);
  doc.text(`Página ${page} de ${totalPages}`, PAGE_W - MARGIN_X, FOOTER_Y, { align: 'right' });
};

const newPage = (ctx: PdfContext, title: string, subtitle: string, data: ConsolidatedReportDTO) => {
  ctx.doc.addPage('a4', 'landscape');
  ctx.page += 1;
  drawPageHeader(ctx, title, subtitle, data);
};

const ensureSpace = (ctx: PdfContext, neededHeight: number, title: string, subtitle: string, data: ConsolidatedReportDTO) => {
  if (ctx.cursorY + neededHeight > PAGE_H - MARGIN_BOTTOM - 5) {
    newPage(ctx, title, subtitle, data);
  }
};

// ─── Bloque de información del informe ───
const drawReportInfo = (ctx: PdfContext, data: ConsolidatedReportDTO) => {
  const { doc } = ctx;
  const fields: Array<[string, string]> = [
    ['Sede', data.SedeNombre || 'Todas'],
    ['Analizador', data.InstrumentoNombre || data.AreaNombre || 'Todos'],
    ['Lote', data.LoteCodigo || 'Todos'],
    ['Generado', formatDateTime(data.FechaGeneracion)],
  ];
  const colW = (PAGE_W - 2 * MARGIN_X) / fields.length;

  setDraw(doc, COLOR.BORDER);
  setFill(doc, [250, 251, 252]);
  doc.rect(MARGIN_X, ctx.cursorY, PAGE_W - 2 * MARGIN_X, 9, 'FD');

  fields.forEach(([label, value], i) => {
    const x = MARGIN_X + colW * i + 2;
    setText(doc, COLOR.SOFT);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text(label.toUpperCase(), x, ctx.cursorY + 3.5);
    setText(doc, COLOR.TEXT);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const truncated = doc.splitTextToSize(value, colW - 4)[0] || '';
    doc.text(truncated, x, ctx.cursorY + 7.5);
  });
  ctx.cursorY += 12;
};

// ─── Render genérico de tabla ───
interface TableColumn<T> {
  header: string;
  width: number; // mm
  align?: 'left' | 'center' | 'right';
  value: (row: T) => string;
  cellFill?: (row: T) => [number, number, number] | null;
  cellTextColor?: (row: T) => [number, number, number] | null;
}

const drawTable = <T,>(
  ctx: PdfContext,
  columns: TableColumn<T>[],
  rows: T[],
  pageTitle: string,
  pageSubtitle: string,
  data: ConsolidatedReportDTO,
  options?: { rowHeight?: number; emptyMessage?: string }
) => {
  const { doc } = ctx;
  const rowH = options?.rowHeight ?? 6;
  const headerH = 7;

  if (rows.length === 0) {
    setText(doc, COLOR.SOFT);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text(options?.emptyMessage || 'Sin datos en el período', MARGIN_X, ctx.cursorY + 6);
    ctx.cursorY += 10;
    return;
  }

  // Header
  ensureSpace(ctx, headerH + rowH * 2, pageTitle, pageSubtitle, data);
  let x = MARGIN_X;
  setFill(doc, COLOR.HEADER_BG);
  doc.rect(MARGIN_X, ctx.cursorY, columns.reduce((s, c) => s + c.width, 0), headerH, 'F');
  setText(doc, [255, 255, 255]);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  columns.forEach((col) => {
    const tx = col.align === 'right' ? x + col.width - 2 : col.align === 'center' ? x + col.width / 2 : x + 2;
    doc.text(col.header, tx, ctx.cursorY + 4.8, { align: col.align || 'left' });
    x += col.width;
  });
  ctx.cursorY += headerH;

  // Rows
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  rows.forEach((row, idx) => {
    ensureSpace(ctx, rowH, pageTitle, pageSubtitle, data);

    if (ctx.cursorY === MARGIN_TOP + 4) {
      // recién entró a una nueva página: redibujar header
      let xh = MARGIN_X;
      setFill(doc, COLOR.HEADER_BG);
      doc.rect(MARGIN_X, ctx.cursorY, columns.reduce((s, c) => s + c.width, 0), headerH, 'F');
      setText(doc, [255, 255, 255]);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      columns.forEach((col) => {
        const tx = col.align === 'right' ? xh + col.width - 2 : col.align === 'center' ? xh + col.width / 2 : xh + 2;
        doc.text(col.header, tx, ctx.cursorY + 4.8, { align: col.align || 'left' });
        xh += col.width;
      });
      ctx.cursorY += headerH;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
    }

    if (idx % 2 === 1) {
      setFill(doc, COLOR.ROW_ALT);
      doc.rect(MARGIN_X, ctx.cursorY, columns.reduce((s, c) => s + c.width, 0), rowH, 'F');
    }

    let xc = MARGIN_X;
    columns.forEach((col) => {
      const fill = col.cellFill?.(row);
      if (fill) {
        setFill(doc, fill);
        doc.rect(xc + 0.4, ctx.cursorY + 0.6, col.width - 0.8, rowH - 1.2, 'F');
      }
      const textColor = col.cellTextColor?.(row);
      setText(doc, textColor || COLOR.TEXT);
      const value = col.value(row);
      const tx = col.align === 'right' ? xc + col.width - 2 : col.align === 'center' ? xc + col.width / 2 : xc + 2;
      doc.text(value, tx, ctx.cursorY + 4.2, { align: col.align || 'left' });
      xc += col.width;
    });

    ctx.cursorY += rowH;
  });

  // Borde inferior de tabla
  setDraw(doc, COLOR.BORDER);
  doc.line(MARGIN_X, ctx.cursorY, MARGIN_X + columns.reduce((s, c) => s + c.width, 0), ctx.cursorY);
  ctx.cursorY += 4;
};

// ─── Página 1: Portada ───
const drawCover = (ctx: PdfContext, data: ConsolidatedReportDTO) => {
  const { doc } = ctx;
  setFill(doc, COLOR.HEADER_BG);
  doc.rect(0, 0, PAGE_W, 36, 'F');

  setText(doc, [255, 255, 255]);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Informe de Control de Calidad', MARGIN_X, 18);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text('Estadística Six Sigma — Resumen Consolidado', MARGIN_X, 28);

  ctx.cursorY = 50;

  setText(doc, COLOR.TEXT);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Información del informe', MARGIN_X, ctx.cursorY);
  ctx.cursorY += 5;

  const fields: Array<[string, string]> = [
    ['Sede', data.SedeNombre || 'Todas'],
    ['Analizador / Instrumento', data.InstrumentoNombre || data.AreaNombre || 'Todos'],
    ['Área', data.AreaNombre || '—'],
    ['Lote', data.LoteCodigo || 'Todos'],
    ['Cliente', data.ClienteNombre || '—'],
    ['Laboratorio', data.LaboratorioNombre || '—'],
    ['Período (desde)', formatDate(data.FechaDesde)],
    ['Período (hasta)', formatDate(data.FechaHasta)],
    ['Fecha de generación', formatDateTime(data.FechaGeneracion)],
  ];

  setDraw(doc, COLOR.BORDER);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  fields.forEach((f) => {
    setFill(doc, [250, 251, 252]);
    doc.rect(MARGIN_X, ctx.cursorY, PAGE_W - 2 * MARGIN_X, 8, 'FD');
    setText(doc, COLOR.SOFT);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(f[0], MARGIN_X + 3, ctx.cursorY + 5.4);
    setText(doc, COLOR.TEXT);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(f[1], MARGIN_X + 80, ctx.cursorY + 5.4);
    ctx.cursorY += 8;
  });

  // Leyenda Sigma
  ctx.cursorY += 6;
  setText(doc, COLOR.TEXT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Leyenda — Bandas Sigma', MARGIN_X, ctx.cursorY);
  ctx.cursorY += 5;

  const legend: Array<[string, [number, number, number]]> = [
    ['≥ 6σ — Clase Mundial', COLOR.WORLD],
    ['5–5.99σ — Excelente', COLOR.EXCELLENT],
    ['4–4.99σ — Bueno', COLOR.GOOD],
    ['3–3.99σ — Mín. aceptable', COLOR.MIN_ACCEPT],
    ['2–2.99σ — Inaceptable', COLOR.UNACCEPT],
    ['< 2σ — Acción inmediata', COLOR.CRITICAL],
  ];
  let lx = MARGIN_X;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  legend.forEach((l) => {
    setFill(doc, l[1]);
    doc.rect(lx, ctx.cursorY, 4, 4, 'F');
    setText(doc, COLOR.TEXT);
    doc.text(l[0], lx + 5, ctx.cursorY + 3.2);
    lx += 47;
  });
};

// ─── Sección: Resumen Sigma ───
const drawSigmaSection = (ctx: PdfContext, data: ConsolidatedReportDTO) => {
  newPage(ctx, 'Informe Six Sigma', '1. Resumen de Desempeño Sigma', data);
  drawReportInfo(ctx, data);

  const resumen = data.ResumenSigma?.ResumenPorMes || [];
  const cols: TableColumn<SigmaResumenMesDTO>[] = [
    { header: 'Mes', width: 30, align: 'left', value: (r) => r.Mes },
    { header: '≥ 6σ', width: 22, align: 'center', value: (r) => String(r.Sigma6), cellFill: () => COLOR.WORLD, cellTextColor: () => [255, 255, 255] },
    { header: '5σ', width: 22, align: 'center', value: (r) => String(r.Sigma5), cellFill: () => COLOR.EXCELLENT, cellTextColor: () => [255, 255, 255] },
    { header: '4σ', width: 22, align: 'center', value: (r) => String(r.Sigma4), cellFill: () => COLOR.GOOD, cellTextColor: () => COLOR.TEXT },
    { header: '3σ', width: 22, align: 'center', value: (r) => String(r.Sigma3), cellFill: () => COLOR.MIN_ACCEPT, cellTextColor: () => [255, 255, 255] },
    { header: '2σ', width: 22, align: 'center', value: (r) => String(r.Sigma2), cellFill: () => COLOR.UNACCEPT, cellTextColor: () => [255, 255, 255] },
    { header: '< 2σ', width: 22, align: 'center', value: (r) => String(r.SigmaMenor2), cellFill: () => COLOR.CRITICAL, cellTextColor: () => [255, 255, 255] },
    {
      header: '% Cumplimiento',
      width: 38,
      align: 'right',
      value: (r) => `${num(r.PorcentajeCumplimiento)}%`,
      cellTextColor: (r) => (r.PorcentajeCumplimiento >= 80 ? COLOR.WORLD : r.PorcentajeCumplimiento >= 60 ? COLOR.MIN_ACCEPT : COLOR.UNACCEPT),
    },
  ];
  drawTable(ctx, cols, resumen, 'Informe Six Sigma', '1. Resumen de Desempeño Sigma', data);

  // Distribución scatter (lista textual de puntos)
  const scatter = data.ResumenSigma?.PuntosScatter || [];
  if (scatter.length > 0) {
    ensureSpace(ctx, 12, 'Informe Six Sigma', '1. Resumen de Desempeño Sigma', data);
    const { doc } = ctx;
    setText(doc, COLOR.TEXT);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Sigma por analito / nivel', MARGIN_X, ctx.cursorY + 4);
    ctx.cursorY += 7;

    const scatterCols: TableColumn<typeof scatter[number]>[] = [
      { header: 'Analito', width: 80, align: 'left', value: (r) => r.AnalitoNombre },
      { header: 'Nivel material', width: 40, align: 'left', value: (r) => r.MaterialNivel },
      { header: 'N', width: 18, align: 'center', value: (r) => String(r.Nivel) },
      {
        header: 'Sigma',
        width: 30,
        align: 'center',
        value: (r) => num(r.SixSigma),
        cellFill: (r) => sigmaBandRGB(r.SixSigma),
        cellTextColor: () => [255, 255, 255],
      },
      { header: 'Clasificación', width: 60, align: 'left', value: (r) => String(r.Clasificacion).replace('_', ' ') },
    ];
    drawTable(ctx, scatterCols, scatter, 'Informe Six Sigma', '1. Resumen de Desempeño Sigma', data);
  }
};

// ─── Sección: Resumen CV ───
const drawCvSection = (ctx: PdfContext, data: ConsolidatedReportDTO) => {
  newPage(ctx, 'Informe Six Sigma', '2. Resumen de Desempeño CV', data);
  drawReportInfo(ctx, data);

  const cols: TableColumn<CvResumenMesDTO>[] = [
    { header: 'Mes', width: 40, align: 'left', value: (r) => r.Mes },
    { header: 'OK CV', width: 35, align: 'center', value: (r) => String(r.CVOK), cellFill: () => COLOR.WORLD, cellTextColor: () => [255, 255, 255] },
    { header: 'ERROR CV', width: 35, align: 'center', value: (r) => String(r.CVError), cellFill: () => COLOR.UNACCEPT, cellTextColor: () => [255, 255, 255] },
    {
      header: '% Cumplimiento',
      width: 50,
      align: 'right',
      value: (r) => `${num(r.PorcentajeCumplimiento)}%`,
      cellTextColor: (r) => (r.PorcentajeCumplimiento >= 80 ? COLOR.WORLD : r.PorcentajeCumplimiento >= 60 ? COLOR.MIN_ACCEPT : COLOR.UNACCEPT),
    },
  ];
  drawTable(ctx, cols, data.ResumenCV?.ResumenPorMes || [], 'Informe Six Sigma', '2. Resumen de Desempeño CV', data);
};

// ─── Sección: Información General ───
const drawGeneralSection = (ctx: PdfContext, data: ConsolidatedReportDTO) => {
  newPage(ctx, 'Informe Six Sigma', '3. Información General', data);
  drawReportInfo(ctx, data);

  const cols: TableColumn<GeneralInfoRowDTO>[] = [
    { header: '#', width: 8, align: 'center', value: (r) => String(r.Fila) },
    { header: 'Analito', width: 38, align: 'left', value: (r) => r.AnalitoNombre },
    { header: 'N', width: 8, align: 'center', value: (r) => String(r.Nivel) },
    { header: '#Datos', width: 14, align: 'center', value: (r) => String(r.NDatos) },
    { header: 'Media', width: 18, align: 'right', value: (r) => num(r.MediaMensual, 3) },
    { header: 'Verdadera', width: 18, align: 'right', value: (r) => num(r.MediaVerdadera, 3) },
    { header: 'Sesgo%', width: 16, align: 'right', value: (r) => num(r.SesgoMensual) },
    { header: 'Sa', width: 14, align: 'right', value: (r) => num(r.Sa) },
    {
      header: 'IS',
      width: 14,
      align: 'right',
      value: (r) => num(r.IS),
      cellTextColor: (r) => indexRGB(r.IS),
    },
    { header: 'CV%', width: 16, align: 'right', value: (r) => num(r.CVMensual) },
    { header: 'CVa', width: 14, align: 'right', value: (r) => num(r.CVa) },
    {
      header: 'ICV',
      width: 14,
      align: 'right',
      value: (r) => num(r.ICV),
      cellTextColor: (r) => indexRGB(r.ICV),
    },
    { header: 'ET', width: 16, align: 'right', value: (r) => num(r.ETMensual) },
    { header: 'ETa', width: 14, align: 'right', value: (r) => num(r.ETa) },
    {
      header: 'IET',
      width: 14,
      align: 'right',
      value: (r) => num(r.IET),
      cellTextColor: (r) => indexRGB(r.IET),
    },
    {
      header: 'Sigma',
      width: 18,
      align: 'center',
      value: (r) => num(r.SixSigma),
      cellFill: (r) => sigmaBandRGB(r.SixSigma),
      cellTextColor: () => [255, 255, 255],
    },
    { header: 'ESC', width: 14, align: 'right', value: (r) => num(r.ESC) },
    { header: 'Clasif.', width: 24, align: 'left', value: (r) => String(r.Clasificacion).replace('_', ' ') },
  ];
  drawTable(ctx, cols, data.InformacionGeneral || [], 'Informe Six Sigma', '3. Información General', data, { rowHeight: 5.5 });
};

// ─── Sección: Indicadores ───
const drawIndicatorsSection = (ctx: PdfContext, data: ConsolidatedReportDTO) => {
  newPage(ctx, 'Informe Six Sigma', '4. Indicadores ICV / IS / IET', data);
  drawReportInfo(ctx, data);

  // Tabla aplanada con Analito, Nivel, ICV, IS, IET
  type FlatRow = { Analito: string; Nivel: number; ICV: number | null; IS: number | null; IET: number | null };
  const flat: FlatRow[] = [];
  (data.Indicadores || []).forEach((a: IndicatorAnalitoDTO) => {
    a.Niveles.forEach((n) => {
      flat.push({ Analito: a.AnalitoNombre, Nivel: n.Nivel, ICV: n.ICV, IS: n.IS, IET: n.IET });
    });
  });

  const cols: TableColumn<FlatRow>[] = [
    { header: 'Analito', width: 100, align: 'left', value: (r) => r.Analito },
    { header: 'Nivel', width: 24, align: 'center', value: (r) => String(r.Nivel) },
    { header: 'ICV', width: 35, align: 'right', value: (r) => num(r.ICV), cellTextColor: (r) => indexRGB(r.ICV) },
    { header: 'IS', width: 35, align: 'right', value: (r) => num(r.IS), cellTextColor: (r) => indexRGB(r.IS) },
    { header: 'IET', width: 35, align: 'right', value: (r) => num(r.IET), cellTextColor: (r) => indexRGB(r.IET) },
  ];
  drawTable(ctx, cols, flat, 'Informe Six Sigma', '4. Indicadores ICV / IS / IET', data);
};

// ─── Helper: extraer set ordenado de meses de un histórico ───
const extractMonths = (rows: Array<{ ValoresPorMes: Record<string, any> }>): string[] => {
  const set = new Set<string>();
  rows.forEach((r) => Object.keys(r.ValoresPorMes || {}).forEach((m) => set.add(m)));
  // Mantener orden insertion-order de Set para preservar el orden del backend
  return Array.from(set);
};

// ─── Sección: Histórico Sigma ───
const drawSigmaHistorySection = (ctx: PdfContext, data: ConsolidatedReportDTO) => {
  newPage(ctx, 'Informe Six Sigma', '5. Histórico Sigma', data);
  drawReportInfo(ctx, data);

  const rows = data.HistoricoSigma || [];
  const months = extractMonths(rows);

  const fixedW = 8 + 60 + 12; // #, Analito, Nivel
  const monthW = months.length > 0 ? Math.max(16, Math.min(28, (PAGE_W - 2 * MARGIN_X - fixedW) / months.length)) : 22;

  const cols: TableColumn<SigmaHistoryRowDTO>[] = [
    { header: '#', width: 8, align: 'center', value: (r) => String(r.Fila) },
    { header: 'Analito', width: 60, align: 'left', value: (r) => r.AnalitoNombre },
    { header: 'Nivel', width: 12, align: 'center', value: (r) => String(r.Nivel) },
    ...months.map<TableColumn<SigmaHistoryRowDTO>>((m) => ({
      header: m,
      width: monthW,
      align: 'center',
      value: (r) => {
        const v = r.ValoresPorMes?.[m];
        return v == null ? '—' : num(v);
      },
      cellFill: (r) => {
        const v = r.ValoresPorMes?.[m];
        return v == null ? null : sigmaBandRGB(v);
      },
      cellTextColor: (r) => {
        const v = r.ValoresPorMes?.[m];
        return v == null ? COLOR.GREY : ([255, 255, 255] as [number, number, number]);
      },
    })),
  ];
  drawTable(ctx, cols, rows, 'Informe Six Sigma', '5. Histórico Sigma', data, { rowHeight: 5.5 });
};

// ─── Sección: Histórico CV ───
const drawCvHistorySection = (ctx: PdfContext, data: ConsolidatedReportDTO) => {
  newPage(ctx, 'Informe Six Sigma', '6. Histórico CV', data);
  drawReportInfo(ctx, data);

  const rows = data.HistoricoCV || [];
  const months = extractMonths(rows);

  const fixedW = 8 + 60 + 12 + 16;
  const monthW = months.length > 0 ? Math.max(16, Math.min(28, (PAGE_W - 2 * MARGIN_X - fixedW) / months.length)) : 22;

  const cols: TableColumn<CvHistoryRowDTO>[] = [
    { header: '#', width: 8, align: 'center', value: (r) => String(r.Fila) },
    { header: 'Analito', width: 60, align: 'left', value: (r) => r.AnalitoNombre },
    { header: 'Nivel', width: 12, align: 'center', value: (r) => String(r.Nivel) },
    { header: 'CVa', width: 16, align: 'right', value: (r) => num(r.CVa) },
    ...months.map<TableColumn<CvHistoryRowDTO>>((m) => ({
      header: m,
      width: monthW,
      align: 'center',
      value: (r) => {
        const cell = r.ValoresPorMes?.[m];
        return cell?.CV == null ? '—' : num(cell.CV);
      },
      cellFill: (r) => {
        const cell = r.ValoresPorMes?.[m];
        if (!cell || cell.CV == null) return null;
        return cell.EsAceptable ? COLOR.WORLD : COLOR.UNACCEPT;
      },
      cellTextColor: (r) => {
        const cell = r.ValoresPorMes?.[m];
        return !cell || cell.CV == null ? COLOR.GREY : ([255, 255, 255] as [number, number, number]);
      },
    })),
  ];
  drawTable(ctx, cols, rows, 'Informe Six Sigma', '6. Histórico CV', data, { rowHeight: 5.5 });
};

// ─── Sección: Planificación QC ───
const drawPlanningSection = (ctx: PdfContext, data: ConsolidatedReportDTO) => {
  newPage(ctx, 'Informe Six Sigma', '7. Planificación de Control de Calidad', data);
  drawReportInfo(ctx, data);

  const cols: TableColumn<QcPlanningRowDTO>[] = [
    { header: '#', width: 10, align: 'center', value: (r) => String(r.Fila) },
    { header: 'Analito', width: 70, align: 'left', value: (r) => r.AnalitoNombre },
    { header: 'Nivel', width: 18, align: 'center', value: (r) => String(r.Nivel) },
    {
      header: 'Sigma',
      width: 24,
      align: 'center',
      value: (r) => num(r.SixSigma),
      cellFill: (r) => sigmaBandRGB(r.SixSigma),
      cellTextColor: () => [255, 255, 255],
    },
    { header: 'Clasificación', width: 40, align: 'left', value: (r) => String(r.Clasificacion).replace('_', ' ') },
    { header: 'Reglas Westgard recomendadas', width: 115, align: 'left', value: (r) => r.ReglasWestgard || '' },
  ];
  drawTable(ctx, cols, data.Planificacion || [], 'Informe Six Sigma', '7. Planificación de Control de Calidad', data);
};

// ─── Punto de entrada principal ───
export const generateConsolidatedReportPdf = (data: ConsolidatedReportDTO, fileName?: string): void => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const ctx: PdfContext = {
    doc,
    cursorY: MARGIN_TOP,
    page: 1,
    totalPages: 0,
  };

  // Página 1: Portada
  drawCover(ctx, data);

  // Secciones
  drawSigmaSection(ctx, data);
  drawCvSection(ctx, data);
  drawGeneralSection(ctx, data);
  drawIndicatorsSection(ctx, data);
  drawSigmaHistorySection(ctx, data);
  drawCvHistorySection(ctx, data);
  drawPlanningSection(ctx, data);

  // Footers (recorrer todas las páginas y poner pie de página)
  ctx.totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= ctx.totalPages; i++) {
    doc.setPage(i);
    ctx.page = i;
    drawPageFooter(ctx);
  }

  const stamp = formatDate(data.FechaDesde).replace(/\//g, '') + '_' + formatDate(data.FechaHasta).replace(/\//g, '');
  doc.save(fileName || `Informe_QC_${stamp || 'consolidado'}.pdf`);
};
